/**
 * Standings rendering helpers for LeagueDetailView.
 *
 * Pure functions extracted from the view: compute per-player match stats from
 * the rounds, and build a standings table row. Kept framework-free (HTML
 * strings) so the view stays focused on orchestration.
 */

import type { Round } from "../../types/index";

export interface PlayerMatchStats {
  played: number;
  wins: number;
}

/** Empty-state row shown when there are no standings yet. */
export const EMPTY_STANDINGS_ROW = `
        <tr>
          <td colspan="6" class="px-6 py-8 text-center text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
            No standings available yet.
          </td>
        </tr>
      `;

/**
 * Compute per-player {played, wins} from reported matches across all rounds.
 * teams[0] is credited the set-1 winner, teams[1] the set-2 winner; matches
 * without sets (e.g. byes) are skipped.
 */
export const computePlayerStats = (rounds: Round[]): Map<number, PlayerMatchStats> => {
  const playerStats = new Map<number, PlayerMatchStats>();

  rounds.forEach((round) => {
    if (!round.matches) return;

    round.matches.forEach((match) => {
      if (match.status === "reported") {
        const sets = typeof match.sets === "string" ? JSON.parse(match.sets) : (match.sets || []);
        if (sets.length === 0) return;

        let p1Sets = 0, p2Sets = 0;
        sets.forEach((s: any) => {
          if (s.pair1score > s.pair2score) p1Sets++;
          else if (s.pair2score > s.pair1score) p2Sets++;
        });

        const p1Won = p1Sets > p2Sets;
        const p2Won = p2Sets > p1Sets;

        const updateStat = (pid: number | undefined | null, won: boolean) => {
          if (!pid) return;
          const stats = playerStats.get(pid) || { played: 0, wins: 0 };
          stats.played++;
          if (won) stats.wins++;
          playerStats.set(pid, stats);
        };

        // teams[0] = team A (credited p1Won), teams[1] = team B (credited p2Won).
        const teams = typeof match.teams === "string" ? JSON.parse(match.teams) : (match.teams || []);
        (teams[0] || []).forEach((pid: number) => updateStat(pid, p1Won));
        (teams[1] || []).forEach((pid: number) => updateStat(pid, p2Won));
      }
    });
  });

  return playerStats;
};

/**
 * Render the inner HTML (avatar + cells) for one standings row. The caller owns
 * the surrounding `<tr>` element and its class.
 */
export const renderStandingRow = (
  standing: any,
  index: number,
  playerStats: Map<number, PlayerMatchStats>
): string => {
  const nameDisplay = standing.displayName || "Unknown";
  // A dropped (withdrawn) entity stays in the table for history but must read as
  // inactive: a muted name plus a "(Dropped)" tag so it's clear they won't be
  // scheduled into new rounds. Only an explicit `isActive === false` counts as
  // dropped; undefined (older payloads) is treated as active.
  const isDropped = standing.isActive === false;
  let avatarDisplay = "";
  let played = standing.matchesPlayed || 0;
  let wins = 0;

  // Determine if this is a pair or individual based on playerIds array
  const playerIds = standing.playerIds || [];
  const isPair = playerIds.length > 1;

  if (isPair) {
    // Fixed Pair Display - Generate double avatar
    // We need to extract initials from the display name "Name1 - Name2"
    const names = nameDisplay.split(" - ");
    const p1Initial = names[0] ? names[0].charAt(0).toUpperCase() : "?";
    const p2Initial = names[1] ? names[1].charAt(0).toUpperCase() : "?";

    avatarDisplay = `
          <div class="flex -space-x-2">
            <div class="size-8 rounded-lg bg-slate-200 dark:bg-slate-700 daylight:bg-day-sunken flex items-center justify-center text-xs font-bold text-slate-900 dark:text-white daylight:text-day-ink ring-2 ring-white dark:ring-surface-dark">
              ${p1Initial}
            </div>
            <div class="size-8 rounded-lg bg-slate-200 dark:bg-slate-700 daylight:bg-day-sunken flex items-center justify-center text-xs font-bold text-slate-900 dark:text-white daylight:text-day-ink ring-2 ring-white dark:ring-surface-dark">
              ${p2Initial}
            </div>
          </div>
        `;

    // Attempt to get wins from local stats if not in API
    // For a fixed pair, both players should have same stats, so take the first one
    if (playerIds[0]) {
      const p1Stats = playerStats.get(parseInt(playerIds[0]));
      if (p1Stats) wins = p1Stats.wins;
    }
  } else {
    // Individual Display - Single avatar
    avatarDisplay = `
          <div class="size-8 rounded-lg bg-slate-200 dark:bg-slate-700 daylight:bg-day-sunken flex items-center justify-center text-xs font-bold text-slate-900 dark:text-white daylight:text-day-ink">
            ${nameDisplay.charAt(0).toUpperCase()}
          </div>
        `;

    // Get wins from local stats
    if (playerIds[0]) {
      const stats = playerStats.get(parseInt(playerIds[0]));
      if (stats) {
        wins = stats.wins;
        played = stats.played;
      }
    }
  }

  const winPct = played > 0 ? (wins / played) * 100 : 0;

  // Dropped rows get a muted, struck-through name and an amber "(Dropped)" tag.
  const nameClass = isDropped
    ? "font-medium text-slate-400 dark:text-slate-500 line-through"
    : "font-medium text-slate-900 dark:text-white";
  const droppedTag = isDropped
    ? '<span class="ml-2 px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 no-underline">Dropped</span>'
    : "";

  return `
        <td class="px-6 py-4 font-bold text-primary daylight:text-day-ink">#${index + 1}</td>
        <td class="px-6 py-4">
          <div class="flex items-center gap-3">
            ${avatarDisplay}
            <span class="${nameClass}">${nameDisplay}</span>${droppedTag}
          </div>
        </td>
        <td class="px-6 py-4 text-center text-slate-700 dark:text-slate-300">${played}</td>
        <td class="px-6 py-4 text-center">
          <span class="px-3 py-1 bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken rounded-full text-sm font-bold text-slate-900 dark:text-white daylight:text-day-ink">
            ${wins}
          </span>
        </td>
        <td class="px-6 py-4 text-center text-slate-700 dark:text-slate-300">${winPct.toFixed(1)}%</td>
        <td class="px-6 py-4 text-center text-slate-700 dark:text-slate-300">${standing.setsWon || 0}</td>
        <td class="px-6 py-4 text-center text-slate-700 dark:text-slate-300">${standing.gamesWon || 0}</td>
        <td class="px-6 py-4 text-center text-slate-700 dark:text-slate-300">${(standing.gameDiff || 0) > 0 ? '+' : ''}${standing.gameDiff || 0}</td>
        <td class="px-6 py-4 text-right">
          <span class="badge badge-success">${standing.points} pts</span>
        </td>
      `;
};
